import { UploadDocument } from '../models/upload-document';
import { StatementOption } from '../models/statement-option';
import { NormalBalance } from '../utils/enums';
import { Select2OptionData } from '@components/shared/select2/select2.interface';

export const INCOME_STATEMENT = 'INCOME_STATEMENT';
export const BALANCE_SHEET = 'BALANCE_SHEET';
export const CALCULATION = 'CALCULATION';
export const CASH_FLOW_STATEMENT = 'CASH_FLOW_STATEMENT';
export const TOTAL_BEGINNING_CASH = 'TOTAL_BEGINNING_CASH';
export const TOTAL_ENDING_CASH = 'TOTAL_ENDING_CASH';
export const UCA_CASHFLOW_DIRECT = 'UCA_CASHFLOW_DIRECT';
export const UCA_CASHFLOW_INDIRECT = 'UCA_CASHFLOW_INDIRECT';
export const DYNAMIC_ADJUSTMENT_STATEMENT = 'DYNAMIC_ADJUSTMENT_STATEMENT';
export const TAX_RETURN = 'TAX_RETURN';
export const OTHER = 'OTHER';

export const UNLABELED_ROW_PREFIX = 'Unlabeled Item';

export const PHYSICAL_DOCUMENT_TYPES = [INCOME_STATEMENT, BALANCE_SHEET, CASH_FLOW_STATEMENT];

export const NON_OTHER_DOCUMENT_TYPES = [
  INCOME_STATEMENT,
  BALANCE_SHEET,
  CASH_FLOW_STATEMENT,
  CALCULATION,
  TAX_RETURN,
];

export const TAXONOMY_ITEM_IDS = {
  CASH_FLOW_STATEMENT: 80000,
  TOTAL_BEGINNING_CASH: 85900,
  TOTAL_ENDING_CASH: 86000,
  UCA_CASHFLOW_DIRECT: 70000,
  UCA_CASHFLOW_INDIRECT: 72900,
  INCOME_STATEMENT: 10000,
  BALANCE_SHEET: 10100,
}

export const TAXONOMY_ID_STATEMENT_TYPE_LOOKUP = {
  80000: CASH_FLOW_STATEMENT,
  85900: TOTAL_BEGINNING_CASH,
  86000: TOTAL_ENDING_CASH,
  70000: UCA_CASHFLOW_DIRECT,
  72900: UCA_CASHFLOW_INDIRECT,
  10000: INCOME_STATEMENT,
  10100: BALANCE_SHEET,
}

export const DYNAMIC_ADJUSTMENT_STATEMENT_ITEM_TYPES = {
  DAS_SECTION_ROOT_NODE: 'dasSectionRootParentNode',
  AS_REPORTED: 'asReported',
  ADJUSTABLE_RAW_ADD_BACK: 'adjustableRawAddBack',
  NON_ADJUSTABLE_RAW_ADD_BACK: 'nonAdjustableRawAddBack',
  MANUAL_USER_INPUT: 'manualUserInput',
  CUSTOM_CALCULATION: 'customCalculation',
  CUSTOM_SECTION_HEADER: 'customSectionHeader'
}

export const PARENT_REF_TO_NORMAL_BALANCE_MAPPINGS = {
  'REF_TOTAL_ASSETS': NormalBalance.Debit,
  'REF_TOTAL_LIABILITIES': NormalBalance.Credit,
  'REF_TOTAL_EQUITY': NormalBalance.Credit,
  'REF_GROSS_PROFIT': NormalBalance.Credit,
  'REF_TOTAL_OPERATING_EXPENSES': NormalBalance.Debit,
  'REF_TOTAL_OTHER_INCOME_EXPENSE': NormalBalance.Credit,
  'REF_OTHER_COMPREHENSIVE_INCOME': NormalBalance.Credit,
}

export const DOCUMENT_TYPES = PHYSICAL_DOCUMENT_TYPES.concat([CALCULATION]);

export const ENV_CONVENTION = {
  PROD_HOST_MARKER: 'prod.fincura.com',
  LEGACY_PROD_HOST: 'app.fincura.com',
  HOST_APPEND: 'app',
  NON_PROD_HOST_APPEND: 'app-',
  API_APPEND: 'api',
  NON_PROD_API_APPEND: 'api-',
  LOCAL: 'local',
  PRODUCTION: 'production',
  LOCAL_PORT: ':8001',
  WHITELABEL_TARGET_HEADER: 'x-fincura-whitelabel-target',
  FINCURA_HOSTING_SUFFIX: 'fincura.com',
  CITI_PROD_HOSTNAME_MATCH: 'app-citi.',
  CITI_PROD_ENV_NAME: 'citi-prod',
  MOODYS_SPREADING_SUFFIX: '.spreading.moodysanalytics.com'
}

export const MONTHLY = 'MONTHLY';
export const QUARTERLY = 'QUARTERLY';
export const ANNUALLY = 'ANNUALLY';
export const SEMI_ANNUALLY = 'SEMI_ANNUALLY';
export const FISCAL_YTD = 'FISCAL_YTD';
export const TTM = 'TTM';

// TODO: cleanup environments that will no longer exist after infra overhaul
export enum LD_IDS {
  // launch darkly ids are per env
  'local' = '5bd1264a10b57d40a20f4e6c',
  'dev' = '6491e4bd5c11881322a0245c',
  'dev2' = '5d6029144c984408804dfbff',
  'qa' = '6491e4cdfec209134d68f401',
  'uat' = '630f881b365409126191fdb2',
  'citi-uat' = '6491e4e05c11881322a0245f',
  'production' = '5bd1264a10b57d40a20f4e6d',
  'citi-prod' = '6491e4f1fec209134d68f404',

  // Regional environments. These names match both the Terraform environments in
  // infra/terraform/accounts/fincura/global/environments/ and the LaunchDarkly
  // environment names.
  'eu-uat' = '687fcfc7c766c1099cad5035',
  'me-uat' = '68f7cc3b3e9c8f50e09f616a',
  'apac-uat' = '68a740efc563ef09ba50705b',
  'eu-production' = '6887f47051ed4509b19e47f3',
  'me-production' = '68f7cc18fa48230aff1972d7',
  'apac-production' = '68a7415f813d5c098fe6d7d1',
}

// Maps the region segment of a spreading.moodysanalytics.com hostname to the prefix used in
// LaunchDarkly and Terraform environment names. AMER is the unprefixed default, so it is
// deliberately absent: 'amer' resolves to 'uat'/'production' rather than 'amer-uat'.
export const LD_REGION_ENV_PREFIXES = {
  'eur': 'eu',
  'me': 'me',
  'apac': 'apac'
}


export const OBFUSCATE_CONFIDENTIAL_INFO_FLAG =
  'obfuscate-confidential-information-in-full-story-session-recordings';

export const DOCUMENT_FILE_TYPE = {
  UNKNOWN: 'UNKNOWN',
  XLSX: 'XLSX'
}

export const STANDARD_ITEM_IDS = {
  NET_INCOME: 62400,
  EBITDA: 62600,
  BALANCE_CHECK: 61100
}

export const DEV_ENV_HOST_MARKERS = [
  'localhost',
  '127.0.0.1',
  'app-dev',
  'app-local'
];

export const LOCAL_ENV_MARKERS = [
  'localhost',
  '127.0.0.1',
  'app-local'
];

/*
export const NOT_HOME_TAB_LINKS = ['/users', '/settings', '/profile'];
export const NOT_USER_TAB_LINKS = ['/company', '/companies-list', '/$'];
*/

export const NB_CHARS = {
  '-': '&#8209;',
};
/* eslint-disable */
export const MATH_ICONS = {
  '+': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50" height="8" width="8"><rect x="0" y="20" width="50" height="10"/><rect x="20" y="0" width="10" height="50"/></svg>',
  '=': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50" height="8" width="8"><rect y="10" width="50" height="10"/><rect y="30" width="50" height="10"/></svg>',
  '-': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50" height="8" width="8"><rect y="20" width="50" height="10"/></svg>',
  '*': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50" height="8" width="8"><line x1="5" x2="45" y1="5" y2="45" stroke="white" stroke-width="10"/><line x1="5" x2="45" y1="45" y2="5" stroke="white" stroke-width="10"/></svg>',
  '/': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50" height="8" width="8"><rect y="20" width="50" height="10"/><rect x="20" rx="10" ry="10" width="10" height="10"/><rect y="40" x="20" rx="10" ry="10" width="10" height="10"/></svg>',
};
/* eslint-enable */
export const OPERATION_TYPE_CHARS = {
  'SUM': '+',
  'SUB': '-',
  'MUL': '*',
  'DIV': '/',
  'EQUAL': '=',
};

export const EXT_TO_ICON = {
  xlsx: 'file-excel',
  xls: 'file-excel',
  pdf: 'file-pdf',
  csv: 'file-excel',
};

export const OPERATION_TYPE_ICONS = {
  'operation': {
    'newEntity': 'fa-plus',
    'changeField': 'fa-pencil-alt',
    'deletedEntity': 'fa-times',
  },
  'validation': {
    'newEntity': 'fa-check',
    'changeField': 'fa-pencil-alt',
    'deletedEntity': 'fa-times',
  },
};

export const ACTION_TYPE_TEXT = {
  'newEntity': 'Added',
  'changeField': 'Edited',
  'deletedEntity': 'Removed',
}

export const DOC_TYPES = {
  [INCOME_STATEMENT]: 'Income Statement',
  [BALANCE_SHEET]: 'Balance Sheet',
  [CASH_FLOW_STATEMENT]: 'Cash Flow Statement',
  [CALCULATION]: 'Covenants',
};

export const DOC_TYPE_DISPLAY_NAMES = {
  INCOME_STATEMENT: 'Income Statement',
  BALANCE_SHEET: 'Balance Sheet',
  CASH_FLOW_STATEMENT: 'Cash Flow Statement',
  CALCULATION: 'Covenants',
  TAX_RETURN: 'Tax Return (PDF only)',
  OTHER: 'Other',
};

export const DOC_TYPE_DISPLAY_NAME_LOOKUP = {
  'INCOME_STATEMENT': 'Income Statement',
  'BALANCE_SHEET': 'Balance Sheet',
  'CASH_FLOW_STATEMENT': 'Cash Flow Statement',
  'CALCULATION': 'Covenants',
  'TAX_RETURN': 'Tax Return',
  'OTHER': 'Other',
}

// additional forms may be added based on feature flags
export const SUPPORTED_TAX_FORMS = ['1065', '1120', '1120S', '1040']
// Supported tax years are restricted to 2018–2025 for all tax forms.
export const SUPPORTED_TAX_YEARS = ['2025', '2024','2023', '2022', '2021', '2020', '2019', '2018']
export const SUPPORTED_TAX_YEARS_BY_FORM = {
  '1065': SUPPORTED_TAX_YEARS,
  '1120': SUPPORTED_TAX_YEARS,
  '1120S': SUPPORTED_TAX_YEARS,
  '1040': SUPPORTED_TAX_YEARS,
  '1040 Schedule C': SUPPORTED_TAX_YEARS,
  'Schedule E': SUPPORTED_TAX_YEARS,
  'Puerto Rico Corporate': SUPPORTED_TAX_YEARS,
  'Puerto Rico Individual': SUPPORTED_TAX_YEARS,
  'Schedule F': SUPPORTED_TAX_YEARS,
}

// used to populate the initial option set of the 'tax year' dropdown before a 'tax form' is selected.
export const DEFAULT_SUPPORTED_TAX_YEARS = ['2025', '2024', '2023', '2022', '2021', '2020', '2019', '2018'];

export const DISPLAY_NAME_1065 = '1065';

export const UPLOAD_DOC_TYPES = [
  new UploadDocument(INCOME_STATEMENT, DOC_TYPE_DISPLAY_NAMES[INCOME_STATEMENT]),
  new UploadDocument(BALANCE_SHEET, DOC_TYPE_DISPLAY_NAMES[BALANCE_SHEET]),
  new UploadDocument(CASH_FLOW_STATEMENT, DOC_TYPE_DISPLAY_NAMES[CASH_FLOW_STATEMENT]),
  new UploadDocument(TAX_RETURN, DOC_TYPE_DISPLAY_NAMES[TAX_RETURN]),
]

export const USER_FRIENDLY_UPLOAD_DOC_TYPES = [
  new UploadDocument(INCOME_STATEMENT, 'Financial Statement(s) (BS, IS, or CF)'),
  new UploadDocument(TAX_RETURN, DOC_TYPE_DISPLAY_NAMES[TAX_RETURN]),
  new UploadDocument(OTHER, DOC_TYPE_DISPLAY_NAMES[OTHER]),
]

export const DOC_TYPES_REVERTED = {
  [INCOME_STATEMENT]: BALANCE_SHEET,
  [BALANCE_SHEET]: INCOME_STATEMENT,
};

export const CAMEL_DOCS = {
  [INCOME_STATEMENT]: 'periodIncome',
  [BALANCE_SHEET]: 'periodBalance',
};

/* eslint-disable */
export const EMAIL_PATTERN = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))\ *$/;
/* eslint-enable */
export const WEBSITE_PATTERN = /^((https?|ftp|smtp):\/\/)?(www.)?[a-z0-9]+\.([a-z0-9]+\.)?[a-z]+(\/)?(\/[a-zA-Z0-9#]+\/?)*$/i;

export const PASSWORD_PATTERN = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])[a-zA-Z0-9!"#$%&'()*+,\-.:;<=>?@\[\\\]^_`{|}~]{8,}$/;

export const PHONE_PATTERN = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}?$/;

export const NAME_PATTERN = /^[a-zA-Z][\w\s,.:;#@$%&'*+—/=?^`{|}~]*$/;

export const DIGIT_PATTERN = /^\d*$/;

export const TEXT_PATTERN = /^[a-zA-Z][\w\s.,:]*$/;

export const NON_WHITESPACE_PATTERN = /^\S*$/;

export const PHONE_MASK = '(000) 000-0000';

export const DATE_MASK = '00/00/0000';

export const DATE_FORMAT = 'MM/DD/YYYY';

export const DATE_LOWER = 'mm/dd/yyyy';

export const SERVER_DATE_FORMAT = 'YYYY-MM-DD';

export const IMAGE_MIME_TYPES = ['image/png', 'image/jpg', 'image/jpeg'];

export const IMAGE_MAX_SIZE = 8388608;

/* eslint-disable */
export const SF_NAMES = ['Accounts_Payable_PrevTTM', 'Accounts_Payable', 'Accounts_Receivable_PrevTTM', 'Accounts_Receivable', 'Adjusted_EDF', 'Assets_Current', 'Assets_Fixed_PP_E', 'Assets_Intangible', 'Assets_Total_Prev_TTM', 'Assets_Total', 'COGS_Depreciation', 'Capex_Spend_Net', 'Capital_Leases', 'Cash_Burn', 'Cash_Flow_From_Operations', 'Cash_on_Balance_Sheet', 'Current_Liabilities', 'Current_Portion_LTD', 'Debt_CapX', 'Debt_Current', 'Debt_Sub', 'Debt', 'Debt_on_BS', 'Depreciation_Amortization', 'EBITDA', 'Goodwill_Net', 'Gross_Profit', 'Intangible_Assets_Net', 'Interest_Charges', 'Inventory_Value_PrevTTM', 'Inventory_Value', 'Liquidation_Hurdle', 'Market_Cap_Valuation_EBITDA_Multiple', 'Market_Cap_Valuation_Revenue_Multiple', 'Market_Cap_Value_Book_Value_Multiple', 'Net_Fixed_Assets', 'Net_Income_Loss', 'Net_Income_PrevTTM', 'Net_Income', 'Operating_Profit', 'Retained_Earnings', 'Revenue_PrevTTM', 'Revenue', 'ST_Notes_Revolver', 'Score_Adjustments_as_of_EBITDA', 'Score_Amort_Sr_Debt_TTM_EBITDA', 'Score_Audit_or_QOE_Method', 'Score_CEO_Tenure', 'Score_CFO_Tenure', 'Score_CapX_Debt_TTM_EBITDA', 'Score_CapX_Debt_as_of_Revenue', 'Score_Cash_as_of_Curr_Liab', 'Score_Change_in_Senior_Management', 'Score_Collateral_Mission_Critical', 'Score_Collateral_Revenue_Generating', 'Score_Collateral_Specialized', 'Score_Custormer_Concentration', 'Score_DSCR_Projected_1_Year', 'Score_DSCR_pro_forma_last_3_years', 'Score_EBITDA_last_3_years', 'Score_EDF', 'Score_Established_Board_of_Directoors', 'Score_Gross_Margin', 'Score_Historical_conduct_with_lenders', 'Score_Intangibles_as_of_Total_Assets', 'Score_Is_Industry_Cyclical', 'Score_LTV_3YR', 'Score_Litigation', 'Score_Long_term_Industry_Outlook', 'Score_Ownership_Available_Capital', 'Score_Ownership_Dry_Powder', 'Score_Ownership_Fund_Size_Net_Worth', 'Score_Ownership_Net_Cash_Investment', 'Score_Ownership_Structure', 'Score_Ownership_Years_in_Investment', 'Score_Pro_Forma_LTD_TTM_EBITDA', 'Score_Projected_1_year_Total_Leverage', 'Score_Projected_EBITDA_Growth', 'Score_Quick_Ratio_Upload', 'Score_Recent_Forebearance_or_Defaul', 'Score_Recurring_Revenue', 'Score_Reliant_on_Single_Product_or_Serv', 'Score_Replaceable_product_service', 'Score_Revenue_past_3_years', 'Score_Seasonality', 'Score_Sole_source_supplier_to_customers', 'Score_Sr_Debt_TTM_EBITDA', 'Score_Supplier_Concentration', 'Score_TNW_past_3_Years', 'Score_Technology_Commodity_Risk', 'Score_Turnaround_Launch_Expansion', 'Score_miss_and_still_1x_DSCR_EOY', 'Senior_Debt', 'Subordinated_Debt', 'Total_Debt_Outstanding', 'Total_Liabilities', 'Total_Long_Term_Liabilities', 'Total_Shares_Outstanding'];
/* eslint-enable */

export const MONTHS_SHORT = ['Jan.', 'Feb.', 'Mar.', 'Apr.', 'May', 'Jun.', 'Jul', 'Aug.', 'Sep.', 'Oct.', 'Nov.', 'Dec.'];

export const WEEKDAYS_SHORT = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

export const PERIOD_TYPES_STRING = {
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly',
  SEMI_ANNUALLY: 'Semi-annual',
  ANNUALLY: 'Annual',
  FISCAL_YTD: 'Fiscal YTD',
  TTM: 'TTM'
};

export const COVENANT_FREQUENCY_OPTIONS = [
  { id: 'MONTHLY', label: 'Monthly' },
  { id: 'QUARTERLY', label: 'Quarterly' },
  { id: 'BIANNUALLY', label: 'Bi-Annually' },
  { id: 'ANNUALLY', label: 'Annually' },
];


export const PERIOD_OBJ = {
  MONTHLY: [
    { id: 1, value: 'January' },
    { id: 2, value: 'February' },
    { id: 3, value: 'March' },
    { id: 4, value: 'April' },
    { id: 5, value: 'May' },
    { id: 6, value: 'June' },
    { id: 7, value: 'July' },
    { id: 8, value: 'August' },
    { id: 9, value: 'September' },
    { id: 10, value: 'October' },
    { id: 11, value: 'November' },
    { id: 12, value: 'December' },
  ],
  QUARTERLY: [
    { id: 1, value: '1st Quarter' },
    { id: 2, value: '2nd Quarter' },
    { id: 3, value: '3rd Quarter' },
    { id: 4, value: '4th Quarter' },
  ],
  SEMI_ANNUALLY: [
    { id: 1, value: '1st Semi-annual' },
    { id: 2, value: '2nd Semi-annual' },
  ],
};

export const PERIOD_TYPES = [
  { index: 1, key: 'MONTHLY', value: 'Monthly', disabled: false },
  { index: 2, key: 'QUARTERLY', value: 'Quarterly', disabled: false },
  { index: 3, key: 'SEMI_ANNUALLY', value: 'Semi-annually', disabled: false },
  { index: 4, key: 'ANNUALLY', value: 'Annually', disabled: false },
];

export const PERIODS = {
  MONTHLY: {
    1: 'January',
    2: 'February',
    3: 'March',
    4: 'April',
    5: 'May',
    6: 'June',
    7: 'Jule',
    8: 'August',
    9: 'September',
    10: 'October',
    11: 'November',
    12: 'December',
  },
  QUARTERLY: {
    1: '1st Quarter',
    2: '2nd Quarter',
    3: '3rd Quarter',
    4: '4th Quarter',
  },
  SEMI_ANNUALLY: {
    1: '1st Semi-annual',
    2: '2nd Semi-annual',
  },
};

export const PERIOD_ARR = ['MONTHLY', 'QUARTERLY', 'SEMI_ANNUALLY', 'ANNUALLY'];

export const PERIOD_CONTENT = {
  MONTHLY: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
  QUARTERLY: [0, 1, 4, 7, 10],
  SEMI_ANNUALLY: [0, 1, 7],
  ANNUALLY: [0, 1],
};
/*

export const PERIOD_MONTH = {
  MONTHLY: 1,
  QUARTERLY: 3,
  SEMI_ANNUALLY: 6,
  ANNUALLY: 12
};
*/


const COVENANT_MSG = {
  header: 'Add your covenant items here.',
  subheader: 'Once data is uploaded you will be able to:',
  info: [
    {
      number: 1,
      text: 'Set up the required values for each covenant under “Compliance” tab.',
    },
    {
      number: 2,
      text: 'Set covenant calculations based on table items.',
    },
  ],
};

export const DOCUMENT_MESSAGE = {
  header: 'Start spreading your statements here.',
  subheader: 'Once you upload statements you will be able to:',
  info: [
    {
      number: 1,
      text: 'Create normalized items.',
    },
    {
      number: 2,
      text: 'Track borrowers\' covenant compliance.',
    },
  ],
};

export const WELCOME_INFO = {
  [CALCULATION]: COVENANT_MSG,
  [INCOME_STATEMENT]: DOCUMENT_MESSAGE,
  [BALANCE_SHEET]: DOCUMENT_MESSAGE,
  [CASH_FLOW_STATEMENT]: DOCUMENT_MESSAGE,
};

export const BLANK_TEMPLATES = {
  'BALANCE_SHEET': [],
  'INCOME_STATEMENT': [],
  'CALCULATION': [],
  'CASH_FLOW_STATEMENT': [],
}

export const BYPASS_CACHE = 'bypass_cache';
export const SWITCH_ON_PROFILER = 'switch_on_profiler';
export const NO_MATCH = 'NONE';
export const TEMP_STANDARDIZED_ITEM_LIST: Array<string> = [
  'revenue',
  'cogs',
  'net income',
  'ebitda',
  'ebitda margin',
  'gross margin',
  'cash',
  'accounts receivable',
  'inventory',
  'current assets',
  'non current assets',
  'total assets',
  'accounts payable',
  'current liabilities',
  'non current liabilities',
  'total equity',
  'total debt',
  'debt',
  NO_MATCH

]

export const UNKNOWN_PREP_LABEL = 'UNKNOWN';

export const EMPTY_ANALYTICS_DATA = {
  periods: [],
  statementDates: [],
  values: {}
};

export const EMPTY_ANALYTICS_ITEM = {
  value: 0,
  formattedValue: '',
  reportingPeriod: '',
  valueType: '',
};

export const UNKNOWN_HTTP_ERROR = 0; // network error or cors issue
export const BAD_REQUEST = 400;
export const UNAUTHORIZED_REQUEST = 401;
export const PAGE_NOT_FOUND = 404;
export const ACCESS_DENIED = 403;
export const INTERNAL_SERVER_ERROR = 500;
export const UNPROCESSABLE_ENTITY = 422;


const NO_BANK_ID_ADMIN_MESSAGE = 'No bank ID parameter from administrator call';
const NO_BANK_ID_MESSAGE = 'No bank ID in request context';

export const ACCEPTABLE_ERROR_MESSAGES = [NO_BANK_ID_ADMIN_MESSAGE, NO_BANK_ID_MESSAGE];
export const DONT_RAISE_API_ERROR_STATUS_CODES = [
  UNKNOWN_HTTP_ERROR,
  INTERNAL_SERVER_ERROR,
  UNPROCESSABLE_ENTITY,
  PAGE_NOT_FOUND,
];

export const LOAN_FORM_OBJECT_FAMILIES = {
  BORROWER_INFO: 'borrowerInfo',
  COMPLIANCE_INFO: 'complianceInfo',
};

export const LOAN_FORM = {
  BORROWER_INFO: {
    NAME: 'name',
    EMAIL: 'email',
    COUNTRY: 'country',
    ADDRESS: 'address',
    CITY: 'city',
    STATE: 'state',
    ZIPCODE: 'zipcode',
    INDUSTRY: 'industry',
    NAICS_CODE: 'naicsCode',
    COMPANY_NOTES: 'companyNotes',
    PAYNET_SCORE: 'paynetScore',
    DUN_BRADSTREET_SCORE: 'dunBradstreetScore',
  },
  COMPLIANCE_INFO: {
    OFAC_SANCTIONS: 'ofacSanctions',
    PACER_BANKRUPTCIES: 'pacerBankruptcies',
    STATE_COURT_CLAIMS: 'stateCourtClaims',
    DIVISIONS_OF_CORP_REPORT: 'divisionsOfCorpReport',
  },
  INTEREST_TYPE: 'interestType',
  BOOKING_STATUS: 'bookingStatus',
  RESIDUAL_VALUE: 'residualValue',
  BALLOON_PAYMENT: 'balloonPayment',
  LAST_PAYMENT_DATE: 'lastPaymentDate',
  START_DATE: 'startDate',
  PAYMENT_TYPE: 'paymentType',
  PAYMENT_STATUS: 'paymentStatus',
  COLLATERAL_VALUE: 'collateralValue',
  COLLATERAL_TYPE: 'collateralType',
  TERM_MONTHS: 'termMonths',
  INTEREST_RATE: 'interestRate',
  TEMPLATE_UUID: 'templateUuid',
  SCORECARD: 'scorecard',
  DESCRIPTION: 'description',
  PRINCIPAL: 'principal',
  TITLE: 'title',
  UUID: 'uuid',
  CONTRACT_TYPE: 'contractType',
}

export const STD_ID_MAPPER = {
  'REF_SALES': 11100,
  'REF_LIVESTOCK_SALES': 11110,
  'REF_CROP_SALES': 11120,
  'REF_MILK_SALES': 11130,
  'REF_COOP_DISTRIBUTIONS': 11140,
  'REF_AGRICULTURAL_PAYMENTS': 11150,
  'REF_OTHER_SALES': 11160,
  'REF_CROP_INSURANCE': 11170,
  'REF_INDIVIDUAL_WAGES': 11500,
  'REF_INDIVIDUAL_INTEREST_INCOME': 11510,
  'REF_INDIVIDUAL_DIVIDEND_INCOME': 11520,
  'REF_INDIVIDUAL_TAXABLE_REFUNDS': 11530,
  'REF_INDIVIDUAL_BUSINESS_INCOME': 11540,
  'REF_INDIVIDUAL_CAPITAL_OTHER_GAINS': 11550,
  'REF_INDIVIDUAL_IRA_DISTRIBUTIONS': 11560,
  'REF_INDIVIDUAL_PENSIONS_AND_ANNUITIES': 11570,
  'REF_INDIVIDUAL_S_CORP_PARTNER_INCOME': 11580,
  'REF_INDIVIDUAL_RE_INCOME': 11590,
  'REF_INDIVIDUAL_SOCIAL_SECURITY_BENEFITS': 11600,
  'REF_INDIVIDUAL_OTHER_INCOME': 11610,
  'REF_RET_DISC': 11200,
  'REF_DISCOUNTS': 11210,
  'REF_OTH_REV': 11300,
  'REF_COGS': 11400,
  'REF_COST_OF_SALES_CONTRACT_LABOR': 11405,
  'REF_COGS_DEPR': 11410,
  'REF_DIRECT_LABOR': 11430,
  'REF_DIRECT_MATERIALS': 11435,
  'REF_DIRECT_MANUFACTURING_OVERHEAD': 11440,
  'REF_REVENUE': 11800,
  'REF_DIRECT_COSTS': 11700,
  'REF_GROSS_PROFIT': 11900,
  'REF_SGA': 12100,
  'REF_PERSONNEL': 12200,
  'REF_OFF_COMP': 12300,
  'REF_RND': 12400,
  'REF_DEPREC_AMORT': 12500,
  'REF_TOTAL_OPERATING_EXPENSES': 12900,
  'REF_NET_OPERATING_PROFIT': 62100,
  'REF_INT_EXP': 13100,
  'REF_FOREX_EXP': 13200,
  'REF_EXTRAORD_ITEMS': 13300,
  'REF_OTH_INC_EXP': 13400,
  'REF_TOTAL_OTHER_INCOME_EXPENSE': 13900,
  'REF_INCOME_BEFORE_TAX': 62200,
  'REF_INCOME_TAXES': 62300,
  'REF_INTEREST_AND_TAXES': 62350,
  'REF_NET_INCOME': 62400,
  'REF_EBIT': 62500,
  'REF_EBITDA': 62600,
  'REF_CASH': 21100,
  'REF_RESTRICTED_CASH': 21200,
  'REF_SHORT_TERM_INVEST': 21300,
  'REF_ACCT_REC_TRADE': 21400,
  'REF_BAD_DEBT_RESERVE': 21500,
  'REF_ACCT_REC_OTHER': 21600,
  'REF_INV_FINISHED_GOODS': 23100,
  'REF_INV_WORK_IN_PROGRESS': 23200,
  'REF_INV_RAW_MATERIALS': 23300,
  'REF_INV_RESERVE': 23400,
  'REF_INVENTORY': 23900,
  'REF_PREPAID_DEFERRED': 21700,
  'REF_WORK_IN_PROGRESS': 21900,
  'REF_DUE_FROM_CP': 22000,
  'REF_NOTES_RECEIVABLE': 22100,
  'REF_OTH_CURRENT_ASSETS': 22200,
  'REF_TOTAL_CURRENT_ASSETS': 22900,
  'REF_PROP_PLANT_EQUIP': 25100,
  'REF_OTH_FIXED_ASSETS': 25200,
  'REF_ACCUM_DEPRECIATION': 25400,
  'REF_FIXED_ASSETS': 25900,
  'REF_GOODWILL': 24100,
  'REF_INTANGIBLES': 24200,
  'REF_DUE_FROM_LT': 24300,
  'REF_OTH_NON_CURRENT_ASSETS': 24400,
  'REF_TOTAL_NON_CURRENT_ASSETS': 42900,
  'REF_TOTAL_ASSETS': 43900,
  'REF_ACCT_PAYABLE_TRADE': 31100,
  'REF_BANK_CREDIT_LINE': 31200,
  'REF_SHORT_TERM_NOTES_PAYABLE': 31300,
  'REF_LONG_TERM_DEBT_LOANS_CP': 31400,
  'REF_LONG_TERM_DEBT_CAP_LEASE_CP': 31500,
  'REF_DEFERRED_REVENUE': 31600,
  'REF_SALES_TAX_PAYABLE': 31700,
  'REF_ACCRUED_LIABILITIES': 31800,
  'REF_OTHER_CURRENT_LIABILITIES': 31900,
  'REF_TOTAL_CURRENT_LIABILITIES': 31950,
  'REF_LONG_TERM_DEBT_LOANS_LT': 32100,
  'REF_LONG_TERM_DEBT_CAP_LEASE_LT': 32200,
  'REF_DUE_TO_LT': 32300,
  'REF_SUBORDINATED_DEBT': 32400,
  'REF_OTH_NON_CURRENT_LIABILITIES': 32500,
  'REF_TOTAL_NON_CURRENT_LIABILITIES': 32900,
  'REF_TOTAL_LIABILITIES': 33900,
  'REF_COMMON_STOCK': 41100,
  'REF_PREFERRED_STOCK': 41200,
  'REF_ADD_PAID_IN_CAPITAL': 41300,
  'REF_OTHER_EQUITY': 41400,
  'REF_RETAINED_EARNINGS': 41500,
  'REF_TREASURY_STOCK': 41600,
  'REF_TOTAL_EQUITY': 41900,
  'REF_TOTAL_LIABILITIES_AND_EQUITY': 61050,
  'REF_CHECK': 61100,
  'REF_WORKING_CAPITAL': 51100,
  'REF_QUICK_RATIO': 51200,
  'REF_CURRENT_RATIO': 51300,
  'REF_NET_WORTH_ACTUAL': 52100,
  'REF_TANGIBLE_NET_WORTH': 52200,
  'REF_DEBT_TO_WORTH': 52300,
  'REF_DEBT_TO_TANGIBLE_NET_WORTH': 52400,
  'REF_LT_DEBT_TO_NET_FIXED_ASSETS': 52500,
  'REF_TOTAL_LIABILITIES_TO_TOTAL_ASSETS': 52600,
  'REF_FUNDED_DEBT_TO_EBITDA': 52700,
  'REF_INTEREST_COVEREAGE': 53100,
  'REF_EBITDA_TO_INTEREST_AND_CPLTD': 53200,
  'REF_RETURN_ON_ASSETS': 64100,
  'REF_RETURN_ON_EQUITY': 64150,
  'REF_GROSS_MARGIN': 64200,
  'REF_OPERATING_EXPENSE_RATIO': 64250,
  'REF_OPERATING_EXPENSE_EXCL_DEPN_RATIO': 64300,
  'REF_OPERATING_PROFIT_MARGIN': 64350,
  'REF_NET_MARGIN': 64400,
  'REF_EBITDA_MARGIN': 64450,
  'REF_NET_ACCOUNTS_RECEIVABLE_DAYS': 73100,
  'REF_INVENTORY_DAYS_ON_HAND': 73150,
  'REF_ACCOUNTS_PAYABLE_DAYS': 73200,
  'REF_CASH_CONVERSION_CYCLE': 73225,
  'REF_NET_SALES_TO_TOTAL_ASSETS': 73250,
  'REF_NET_SALES_TO_NET_WORTH': 73300,
  'REF_NET_SALES_TO_FIXED_ASSETS': 73350,
  'REF_OTHER_COMPREHENSIVE_INCOME': 35100,
};

export const COGS_ITEMS_THAT_ARE_IN_LEGACY_REVENUE_SECTION = {
  'REF_COGS': 11400,
  'REF_COST_OF_SALES_CONTRACT_LABOR': 11405,
  'REF_COGS_DEPR': 11410,
  'REF_DIRECT_LABOR': 11430,
  'REF_DIRECT_MATERIALS': 11435,
  'REF_DIRECT_MANUFACTURING_OVERHEAD': 11440,
}

export const IS_MAC = /(Mac|iPhone|iPod|iPad)/i.test(navigator.platform);

// document file steps
export const FILE_TYPE_IDENTIFICATION_STEP = 'FileTypeIdentification';
export const COMMENTARY_FILTERING_STEP = 'CommentaryFiltering';
export const COMMENTARY_VALIDATION_STEP = 'CommentaryValidation';
export const COMPLETE_STEP = 'Complete';
export const IMAGE_GENERATION_STEP = 'ImageGeneration';
export const STATEMENT_CLASSIFICATION_STEP = 'StatementClassification';
export const STATEMENT_REVIEW_STEP = 'StatementReview';
export const TABLE_AGGREGATION_STEP = 'TableAggregation';
export const TABLE_AGGREGATION_VALIDATION_STEP = 'TableAggregationValidation';
export const TABLE_IDENTIFICATION_STEP = 'TableIdentification';
export const TABLE_ID_VALIDATION_STEP = 'TableIDValidation';
export const AZURE_DOCUMENT_INTELLIGENCE_EARLY_CLASSIFICATION_UI_FLAG = 'azure_document_intelligence';
// LLM-powered spreading steps gated for the AI disclaimer banner (AUTO-3408).
// Format Data step (bedrock Nova Pro table-metadata extraction) / Review Mappings
// step (pass-1 LLM autonorm categorization). Both must be client-side available in
// LaunchDarkly for the browser SDK to read them.
export const BEDROCK_TABLE_METADATA_FLAG = 'bedrock-table-metadata';
export const LLM_AUTONORM_CATEGORIZATION_FLAG = 'llm-autonorm-categorization-v1';
export const VENDOR_DATA_UI_FLAG = 'vendor-data-ui';
export const STEP_LABELS = {
  TABLE_ID:           { legacy: 'Identify Tables',      earlyClass: 'Classify Tables'  },
  TABLE_ID_FORMAL:    { legacy: 'Table Identification',  earlyClass: 'Classify Tables'  },
  DATA_REVIEW:        { legacy: 'Review Data',           earlyClass: 'Format Data'      },
  DATA_REVIEW_FORMAL: { legacy: 'Data Review',           earlyClass: 'Format Data'      },
  SPREAD:             { legacy: 'Categorize Data',       earlyClass: 'Review Mappings'  },
  SPREAD_FORMAL:      { legacy: 'Data Categorization',   earlyClass: 'Review Mappings'  },
} as const;
export const TABLE_TABULATION_STEP = 'TableTabulation';
export const TABLE_TABULATION_VALIDATION_STEP = 'TableTabulationValidation';
export const TEXT_EXTRACTION_STEP = 'TextExtraction';
export const PREP_FOR_MANUAL_REVIEW_STEP = 'PrepForManualReview';
export const FINAL_REVIEW_STEP = 'ManualReview';
export const SPREADING_STEP = 'Spreading';
export const ADDITIONAL_DETAILS_NEEDED_STEP = 'AdditionalDetailsNeeded';
export const SUCCEEDED_STATUS = 'SUCCEEDED';
export const DOCUMENT_PASSWORD_ENTRY_REQUIRED = 'DocumentPasswordEntry';

// review queue item statuses
export const PENDING_STATUS = 'PENDING';
export const UPLOADED_STATUS = 'UPLOADED';
export const QUEUED_FOR_PROCESSING_STATUS = 'QUEUED_FOR_PROCESSING';
export const DOC_PROCESSING_STATUS = 'DOC_PROCESSING';
export const COMPLETE_STATUS = 'COMPLETE';
export const DELETED_STATUS = 'DELETED';
export const ERROR_STATUS = 'ERROR';
export const MALICIOUS_FILE_STATUS = 'MALICIOUS_FILE';
export const IN_REVIEW_STATUS = 'IN_REVIEW';
export const HUMAN_INPUT_REQUIRED_STATUS = 'HUMAN_INPUT_REQUIRED';
export const DOC_PROCESSED_STATUS = 'DOC_PROCESSED';
export const CHANGES_SAVED_STATUS = 'CHANGES_SAVED';
export const REVIEWED_STATUS = 'REVIEWED';
export const NORMALIZED_STATUS = 'NORMALIZED';

// these are the more human readable rqi statuses attached to document file object
export const PENDING_STATUS_LABEL = 'Pending';
export const PROCESSING_STATUS_LABEL = 'Processing';
export const HUMAN_INPUT_REQUIRED_STATUS_LABEL = 'Human Input Required';
export const COMPLETE_STATUS_LABEL = 'Complete';
export const REVIEWED_STATUS_LABEL = 'Reviewed';
export const CHANGES_SAVED_STATUS_LABEL = 'Changes Saved';
export const UKNOWN_STATUS_LABEL = 'Unknown';
export const OTHER_DOC_UPLOADED = 'Document Uploaded';

export const DEFAULT_CURRENCY = 'USD';

export const SUPPORTED_CURRENCIES = [
  {
    "key": "USD",
    "numeric": "840",
    "name": "United States Dollar",
    "shortName": "Dollar",
    "decimals": 2,
    "symbol": "$"
  },
  {
    "key": "AED",
    "numeric": "784",
    "name": "United Arab Emirates Dirham",
    "shortName": "Dirham",
    "decimals": 2,
    "symbol": "د"
  },
  {
    "key": "AFN",
    "numeric": "971",
    "name": "Afghan Afghani",
    "shortName": "Afghani",
    "decimals": 2,
    "symbol": "؋"
  },
  {
    "key": "ALL",
    "numeric": "008",
    "name": "Albanian Lek",
    "shortName": "Lek",
    "decimals": 2,
    "symbol": "Lek"
  },
  {
    "key": "AMD",
    "numeric": "051",
    "name": "Armenian Dram",
    "shortName": "Dram",
    "decimals": 2,
    "symbol": "֏"
  },
  {
    "key": "ANG",
    "numeric": "532",
    "name": "Netherlands Antillean Guilder",
    "shortName": "Guilder",
    "decimals": 2,
    "symbol": "ƒ"
  },
  {
    "key": "AOA",
    "numeric": "973",
    "name": "Angolan Kwanza",
    "shortName": "Kwanza",
    "decimals": 2,
    "symbol": "Kz"
  },
  {
    "key": "ARS",
    "numeric": "032",
    "name": "Argentine Peso",
    "shortName": "Peso",
    "decimals": 2,
    "symbol": "$"
  },
  {
    "key": "AUD",
    "numeric": "036",
    "name": "Australian Dollar",
    "shortName": "Dollar",
    "decimals": 2,
    "symbol": "$"
  },
  {
    "key": "AWG",
    "numeric": "533",
    "name": "Aruban Florin",
    "shortName": "Florin",
    "decimals": 2,
    "symbol": "ƒ"
  },
  {
    "key": "AZN",
    "numeric": "944",
    "name": "Azerbaijani Manat",
    "shortName": "Manat",
    "decimals": 2,
    "symbol": "₼"
  },
  {
    "key": "BAM",
    "numeric": "977",
    "name": "Bosnia-Herzegovina Convertible Mark",
    "shortName": "Convertible Mark",
    "decimals": 2,
    "symbol": "KM"
  },
  {
    "key": "BBD",
    "numeric": "052",
    "name": "Barbadian Dollar",
    "shortName": "Dollar",
    "decimals": 2,
    "symbol": "$"
  },
  {
    "key": "BDT",
    "numeric": "050",
    "name": "Bangladeshi Taka",
    "shortName": "Taka",
    "decimals": 2,
    "symbol": "৳"
  },
  {
    "key": "BGN",
    "numeric": "975",
    "name": "Bulgarian Lev",
    "shortName": "Lev",
    "decimals": 2,
    "symbol": "лв"
  },
  {
    "key": "BHD",
    "numeric": "048",
    "name": "Bahraini Dinar",
    "shortName": "Dinar",
    "decimals": 3,
    "symbol": "د"
  },
  {
    "key": "BIF",
    "numeric": "108",
    "name": "Burundian Franc",
    "shortName": "Franc",
    "decimals": 2,
    "symbol": "Franc"
  },
  {
    "key": "BMD",
    "numeric": "060",
    "name": "Bermudan Dollar",
    "shortName": "Dollar",
    "decimals": 2,
    "symbol": "$"
  },
  {
    "key": "BND",
    "numeric": "096",
    "name": "Brunei Dollar",
    "shortName": "Dollar",
    "decimals": 2,
    "symbol": "$"
  },
  {
    "key": "BOB",
    "numeric": "068",
    "name": "Bolivian Boliviano",
    "shortName": "Boliviano",
    "decimals": 2,
    "symbol": "$b"
  },
  {
    "key": "BRL",
    "numeric": "986",
    "name": "Brazilian Real",
    "shortName": "Real",
    "decimals": 2,
    "symbol": "R$"
  },
  {
    "key": "BSD",
    "numeric": "044",
    "name": "Bahamian Dollar",
    "shortName": "Dollar",
    "decimals": 2,
    "symbol": "$"
  },
  {
    "key": "BTN",
    "numeric": "064",
    "name": "Bhutanese Ngultrum",
    "shortName": "Ngultrum",
    "decimals": 2,
    "symbol": "Nu"
  },
  {
    "key": "BWP",
    "numeric": "072",
    "name": "Botswanan Pula",
    "shortName": "Pula",
    "decimals": 2,
    "symbol": "P"
  },
  {
    "key": "BYN",
    "numeric": "933",
    "name": "Belarusian Ruble",
    "shortName": "Ruble",
    "decimals": 2,
    "symbol": "Br"
  },
  {
    "key": "BYR",
    "name": "Belarusian Ruble",
    "shortName": "Ruble",
    "decimals": 2,
    "symbol": "Br"
  },
  {
    "key": "BZD",
    "numeric": "084",
    "name": "Belize Dollar",
    "shortName": "Dollar",
    "decimals": 2,
    "symbol": "BZ$"
  },
  {
    "key": "CAD",
    "numeric": "124",
    "name": "Canadian Dollar",
    "shortName": "Dollar",
    "decimals": 2,
    "symbol": "$"
  },
  {
    "key": "CDF",
    "numeric": "976",
    "name": "Congolese Franc",
    "shortName": "Franc",
    "decimals": 2,
    "symbol": "Franc"
  },
  {
    "key": "CHE",
    "numeric": "947",
    "name": "WIR Euro",
    "symbol": "€"
  },
  {
    "key": "CHF",
    "numeric": "756",
    "name": "Swiss Franc",
    "shortName": "Franc",
    "decimals": 2,
    "symbol": "CHF"
  },
  {
    "key": "CHW",
    "numeric": "948",
    "name": "WIR Franc",
    "symbol": "Franc"
  },
  {
    "key": "CLF",
    "numeric": "990",
    "name": "Unidad de Fomento",
    "symbol": "$"
  },
  {
    "key": "CLP",
    "numeric": "152",
    "name": "Chilean Peso",
    "shortName": "Peso",
    "decimals": 2,
    "symbol": "$"
  },
  {
    "key": "CNH",
    "name": "Chinese Yuan Renminbi",
    "shortName": "Yuan",
    "decimals": 2,
    "symbol": "¥"
  },
  {
    "key": "CNY",
    "numeric": "156",
    "name": "Chinese Yuan Renminbi",
    "shortName": "Yuan",
    "decimals": 2,
    "symbol": "¥"
  },
  {
    "key": "COP",
    "numeric": "170",
    "name": "Colombian Peso",
    "shortName": "Peso",
    "decimals": 2,
    "symbol": "$"
  },
  {
    "key": "COU",
    "numeric": "970",
    "name": "Unidad de Valor Real",
    "symbol": "UVR"
  },
  {
    "key": "CRC",
    "numeric": "188",
    "name": "Costa Rican Colón",
    "shortName": "Colón",
    "decimals": 2,
    "symbol": "₡"
  },
  {
    "key": "CUC",
    "numeric": "931",
    "name": "Cuban Convertible Peso",
    "shortName": "Convertible Peso",
    "decimals": 2,
    "symbol": "$"
  },
  {
    "key": "CUP",
    "numeric": "192",
    "name": "Cuban Peso",
    "shortName": "Peso",
    "decimals": 2,
    "symbol": "₱"
  },
  {
    "key": "CVE",
    "numeric": "132",
    "name": "Cape Verdean Escudo",
    "shortName": "Escudo",
    "decimals": 2,
    "symbol": "Esc"
  },
  {
    "key": "CYP",
    "name": "Cypriot Pound",
    "symbol": "£"
  },
  {
    "key": "CZK",
    "numeric": "203",
    "name": "Czech Republic Koruna",
    "shortName": "Koruna",
    "decimals": 2,
    "symbol": "Kč"
  },
  {
    "key": "DJF",
    "numeric": "262",
    "name": "Djiboutian Franc",
    "shortName": "Franc",
    "decimals": 2,
    "symbol": "Franc"
  },
  {
    "key": "DKK",
    "numeric": "208",
    "name": "Danish Krone",
    "shortName": "Krone",
    "decimals": 2,
    "symbol": "kr"
  },
  {
    "key": "DOP",
    "numeric": "214",
    "name": "Dominican Peso",
    "shortName": "Peso",
    "decimals": 2,
    "symbol": "RD$"
  },
  {
    "key": "DZD",
    "numeric": "012",
    "name": "Algerian Dinar",
    "shortName": "Dinar",
    "decimals": 2,
    "symbol": "د"
  },
  {
    "key": "EEK",
    "name": "Estonian Kroon",
    "symbol": "kr"
  },
  {
    "key": "EGP",
    "numeric": "818",
    "name": "Egyptian Pound",
    "shortName": "Pound",
    "decimals": 2,
    "symbol": "£"
  },
  {
    "key": "ERN",
    "numeric": "232",
    "name": "Eritrean Nakfa",
    "shortName": "Nakfa",
    "decimals": 2,
    "symbol": "Nfk"
  },
  {
    "key": "ETB",
    "numeric": "230",
    "name": "Ethiopian Birr",
    "shortName": "Birr",
    "decimals": 2,
    "symbol": "Br"
  },
  {
    "key": "EUR",
    "numeric": "978",
    "name": "Euro",
    "shortName": "Euro",
    "decimals": 2,
    "symbol": "€"
  },
  {
    "key": "FJD",
    "numeric": "242",
    "name": "Fijian Dollar",
    "shortName": "Dollar",
    "decimals": 2,
    "symbol": "$"
  },
  {
    "key": "FKP",
    "numeric": "238",
    "name": "Falkland Islands Pound",
    "shortName": "Pound",
    "decimals": 2,
    "symbol": "£"
  },
  {
    "key": "GBP",
    "numeric": "826",
    "name": "British Pound Sterling",
    "shortName": "Sterling",
    "decimals": 2,
    "symbol": "£"
  },
  {
    "key": "GEL",
    "numeric": "981",
    "name": "Georgian Lari",
    "shortName": "Lari",
    "decimals": 2,
    "symbol": "₾"
  },
  {
    "key": "GHS",
    "numeric": "936",
    "name": "Ghanaian Cedi",
    "shortName": "Cedi",
    "decimals": 2,
    "symbol": "¢"
  },
  {
    "key": "GIP",
    "numeric": "292",
    "name": "Gibraltar Pound",
    "shortName": "Pound",
    "decimals": 2,
    "symbol": "£"
  },
  {
    "key": "GMD",
    "numeric": "270",
    "name": "Gambian Dalasi",
    "shortName": "Dalasi",
    "decimals": 2,
    "symbol": "D"
  },
  {
    "key": "GNF",
    "numeric": "324",
    "name": "Guinean Franc",
    "shortName": "Franc",
    "decimals": 2,
    "symbol": "Franc"
  },
  {
    "key": "GTQ",
    "numeric": "320",
    "name": "Guatemalan Quetzal",
    "shortName": "Quetzal",
    "decimals": 2,
    "symbol": "Q"
  },
  {
    "key": "GYD",
    "numeric": "328",
    "name": "Guyanaese Dollar",
    "shortName": "Dollar",
    "decimals": 2,
    "symbol": "$"
  },
  {
    "key": "HKD",
    "numeric": "344",
    "name": "Hong Kong Dollar",
    "shortName": "Dollar",
    "decimals": 2,
    "symbol": "$"
  },
  {
    "key": "HNL",
    "numeric": "340",
    "name": "Honduran Lempira",
    "shortName": "Lempira",
    "decimals": 2,
    "symbol": "L"
  },
  {
    "key": "HRK",
    "numeric": "191",
    "name": "Croatian Kuna",
    "shortName": "Kuna",
    "decimals": 2,
    "symbol": "kn"
  },
  {
    "key": "HTG",
    "numeric": "332",
    "name": "Haitian Gourde",
    "shortName": "Gourde",
    "decimals": 2,
    "symbol": "G"
  },
  {
    "key": "HUF",
    "numeric": "348",
    "name": "Hungarian Forint",
    "shortName": "Forint",
    "decimals": 2,
    "symbol": "Ft"
  },
  {
    "key": "IDR",
    "numeric": "360",
    "name": "Indonesian Rupiah",
    "shortName": "Rupiah",
    "decimals": 2,
    "symbol": "Rp"
  },
  {
    "key": "ILS",
    "numeric": "376",
    "name": "Israeli New Sheqel",
    "shortName": "New Sheqel",
    "decimals": 2,
    "symbol": "₪"
  },
  {
    "key": "INR",
    "numeric": "356",
    "name": "Indian Rupee",
    "shortName": "Rupee",
    "decimals": 2,
    "symbol": "₹"
  },
  {
    "key": "IQD",
    "numeric": "368",
    "name": "Iraqi Dinar",
    "shortName": "Dinar",
    "decimals": 3,
    "symbol": "ع"
  },
  {
    "key": "IRR",
    "numeric": "364",
    "name": "Iranian Rial",
    "shortName": "Rial",
    "decimals": 2,
    "symbol": "﷼"
  },
  {
    "key": "ISK",
    "numeric": "352",
    "name": "Icelandic Króna",
    "shortName": "Króna",
    "decimals": 2,
    "symbol": "kr"
  },
  {
    "key": "JMD",
    "numeric": "388",
    "name": "Jamaican Dollar",
    "shortName": "Dollar",
    "decimals": 2,
    "symbol": "J$"
  },
  {
    "key": "JOD",
    "numeric": "400",
    "name": "Jordanian Dinar",
    "shortName": "Dinar",
    "decimals": 2,
    "symbol": "د"
  },
  {
    "key": "JPY",
    "numeric": "392",
    "name": "Japanese Yen",
    "shortName": "Yen",
    "decimals": 2,
    "symbol": "¥"
  },
  {
    "key": "KES",
    "numeric": "404",
    "name": "Kenyan Shilling",
    "shortName": "Shilling",
    "decimals": 2,
    "symbol": "Sh"
  },
  {
    "key": "KGS",
    "numeric": "417",
    "name": "Kyrgystani Som",
    "shortName": "Som",
    "decimals": 2,
    "symbol": "лв"
  },
  {
    "key": "KHR",
    "numeric": "116",
    "name": "Cambodian Riel",
    "shortName": "Riel",
    "decimals": 2,
    "symbol": "៛"
  },
  {
    "key": "KMF",
    "numeric": "174",
    "name": "Comorian Franc",
    "shortName": "Franc",
    "decimals": 2,
    "symbol": "Franc"
  },
  {
    "key": "KPW",
    "numeric": "408",
    "name": "North Korean Won",
    "shortName": "Won",
    "decimals": 2,
    "symbol": "₩"
  },
  {
    "key": "KRW",
    "numeric": "410",
    "name": "South Korean Won",
    "shortName": "Won",
    "decimals": 2,
    "symbol": "₩"
  },
  {
    "key": "KWD",
    "numeric": "414",
    "name": "Kuwaiti Dinar",
    "shortName": "Dinar",
    "decimals": 3,
    "symbol": "د"
  },
  {
    "key": "KYD",
    "numeric": "136",
    "name": "Cayman Islands Dollar",
    "shortName": "Dollar",
    "decimals": 2,
    "symbol": "$"
  },
  {
    "key": "KZT",
    "numeric": "398",
    "name": "Kazakhstani Tenge",
    "shortName": "Tenge",
    "decimals": 2,
    "symbol": "лв"
  },
  {
    "key": "LAK",
    "numeric": "418",
    "name": "Laotian Kip",
    "shortName": "Kip",
    "decimals": 2,
    "symbol": "₭"
  },
  {
    "key": "LBP",
    "numeric": "422",
    "name": "Lebanese Pound",
    "shortName": "Pound",
    "decimals": 2,
    "symbol": "£"
  },
  {
    "key": "LKR",
    "numeric": "144",
    "name": "Sri Lankan Rupee",
    "shortName": "Rupee",
    "decimals": 2,
    "symbol": "₨"
  },
  {
    "key": "LRD",
    "numeric": "430",
    "name": "Liberian Dollar",
    "shortName": "Dollar",
    "decimals": 2,
    "symbol": "$"
  },
  {
    "key": "LSL",
    "numeric": "426",
    "name": "Lesotho Loti",
    "shortName": "Loti",
    "decimals": 2,
    "symbol": "L"
  },
  {
    "key": "LTL",
    "name": "Lithuanian Litas",
    "symbol": "Lt"
  },
  {
    "key": "LVL",
    "name": "Latvian Lat",
    "symbol": "Ls"
  },
  {
    "key": "LYD",
    "numeric": "434",
    "name": "Libyan Dinar",
    "shortName": "Dinar",
    "decimals": 3,
    "symbol": "ل"
  },
  {
    "key": "MAD",
    "numeric": "504",
    "name": "Moroccan Dirham",
    "shortName": "Dirham",
    "decimals": 2,
    "symbol": "د"
  },
  {
    "key": "MDL",
    "numeric": "498",
    "name": "Moldovan Leu",
    "shortName": "Leu",
    "decimals": 2,
    "symbol": "L"
  },
  {
    "key": "MGA",
    "numeric": "969",
    "name": "Malagasy Ariary",
    "shortName": "Ariary",
    "decimals": 1,
    "symbol": "Ar"
  },
  {
    "key": "MKD",
    "numeric": "807",
    "name": "Macedonian Denar",
    "shortName": "Denar",
    "decimals": 2,
    "symbol": "ден"
  },
  {
    "key": "MMK",
    "numeric": "104",
    "name": "Myanma Kyat",
    "shortName": "Kyat",
    "decimals": 2,
    "symbol": "Ks"
  },
  {
    "key": "MNT",
    "numeric": "496",
    "name": "Mongolian Tugrik",
    "shortName": "Tugrik",
    "decimals": 2,
    "symbol": "₮"
  },
  {
    "key": "MOP",
    "numeric": "446",
    "name": "Macanese Pataca",
    "shortName": "Pataca",
    "decimals": 2,
    "symbol": "P"
  },
  {
    "key": "MRU",
    "numeric": "929",
    "name": "Mauritanian Ouguiya",
    "shortName": "Ouguiya",
    "symbol": "UM"
  },
  {
    "key": "MUR",
    "numeric": "480",
    "name": "Mauritian Rupee",
    "shortName": "Rupee",
    "decimals": 2,
    "symbol": "₨"
  },
  {
    "key": "MVR",
    "numeric": "462",
    "name": "Maldivian Rufiyaa",
    "shortName": "Rufiyaa",
    "decimals": 2,
    "symbol": "ރ"
  },
  {
    "key": "MWK",
    "numeric": "454",
    "name": "Malawian Kwacha",
    "shortName": "Kwacha",
    "decimals": 2,
    "symbol": "MK"
  },
  {
    "key": "MXN",
    "numeric": "484",
    "name": "Mexican Peso",
    "shortName": "Peso",
    "decimals": 2,
    "symbol": "$"
  },
  {
    "key": "MXV",
    "numeric": "979",
    "name": "Mexican Unidad de Inversion",
    "symbol": "UDI"
  },
  {
    "key": "MYR",
    "numeric": "458",
    "name": "Malaysian Ringgit",
    "shortName": "Ringgit",
    "decimals": 2,
    "symbol": "RM"
  },
  {
    "key": "MZN",
    "numeric": "943",
    "name": "Mozambican Metical",
    "shortName": "Metical",
    "decimals": 2,
    "symbol": "MT"
  },
  {
    "key": "NAD",
    "numeric": "516",
    "name": "Namibian Dollar",
    "shortName": "Dollar",
    "decimals": 2,
    "symbol": "$"
  },
  {
    "key": "NGN",
    "numeric": "566",
    "name": "Nigerian Naira",
    "shortName": "Naira",
    "decimals": 2,
    "symbol": "₦"
  },
  {
    "key": "NIO",
    "numeric": "558",
    "name": "Nicaraguan Córdoba",
    "shortName": "Córdoba",
    "decimals": 2,
    "symbol": "C$"
  },
  {
    "key": "NOK",
    "numeric": "578",
    "name": "Norwegian Krone",
    "shortName": "Krone",
    "decimals": 2,
    "symbol": "kr"
  },
  {
    "key": "NPR",
    "numeric": "524",
    "name": "Nepalese Rupee",
    "shortName": "Rupee",
    "decimals": 2,
    "symbol": "₨"
  },
  {
    "key": "NZD",
    "numeric": "554",
    "name": "New Zealand Dollar",
    "shortName": "Dollar",
    "decimals": 2,
    "symbol": "$"
  },
  {
    "key": "OMR",
    "numeric": "512",
    "name": "Omani Rial",
    "shortName": "Rial",
    "decimals": 3,
    "symbol": "﷼"
  },
  {
    "key": "PAB",
    "numeric": "590",
    "name": "Panamanian Balboa",
    "shortName": "Balboa",
    "decimals": 2,
    "symbol": "B/."
  },
  {
    "key": "PEN",
    "numeric": "604",
    "name": "Peruvian Sol",
    "shortName": "Sol",
    "decimals": 2,
    "symbol": "S/."
  },
  {
    "key": "PGK",
    "numeric": "598",
    "name": "Papua New Guinean Kina",
    "shortName": "Kina",
    "decimals": 2,
    "symbol": "K"
  },
  {
    "key": "PHP",
    "numeric": "608",
    "name": "Philippine Peso",
    "shortName": "Peso",
    "decimals": 2,
    "symbol": "₱"
  },
  {
    "key": "PKR",
    "numeric": "586",
    "name": "Pakistani Rupee",
    "shortName": "Rupee",
    "decimals": 2,
    "symbol": "₨"
  },
  {
    "key": "PLN",
    "numeric": "985",
    "name": "Polish Zloty",
    "shortName": "Zloty",
    "decimals": 2,
    "symbol": "zł"
  },
  {
    "key": "PYG",
    "numeric": "600",
    "name": "Paraguayan Guarani",
    "shortName": "Guarani",
    "decimals": 2,
    "symbol": "Gs"
  },
  {
    "key": "QAR",
    "numeric": "634",
    "name": "Qatari Rial",
    "shortName": "Rial",
    "decimals": 2,
    "symbol": "﷼"
  },
  {
    "key": "RON",
    "numeric": "946",
    "name": "Romanian Leu",
    "shortName": "Leu",
    "decimals": 2,
    "symbol": "lei"
  },
  {
    "key": "RSD",
    "numeric": "941",
    "name": "Serbian Dinar",
    "shortName": "Dinar",
    "decimals": 2,
    "symbol": "Дин."
  },
  {
    "key": "RUB",
    "numeric": "643",
    "name": "Russian Ruble",
    "shortName": "Ruble",
    "decimals": 2,
    "symbol": "₽"
  },
  {
    "key": "RWF",
    "numeric": "646",
    "name": "Rwandan Franc",
    "shortName": "Franc",
    "decimals": 2,
    "symbol": "Franc"
  },
  {
    "key": "SAR",
    "numeric": "682",
    "name": "Saudi Riyal",
    "shortName": "Riyal",
    "decimals": 2,
    "symbol": "﷼"
  },
  {
    "key": "SBD",
    "numeric": "090",
    "name": "Solomon Islands Dollar",
    "shortName": "Dollar",
    "decimals": 2,
    "symbol": "$"
  },
  {
    "key": "SCR",
    "numeric": "690",
    "name": "Seychellois Rupee",
    "shortName": "Rupee",
    "decimals": 2,
    "symbol": "₨"
  },
  {
    "key": "SDG",
    "numeric": "938",
    "name": "Sudanese Pound",
    "shortName": "Pound",
    "decimals": 2,
    "symbol": "ج"
  },
  {
    "key": "SEK",
    "numeric": "752",
    "name": "Swedish Krona",
    "shortName": "Krona",
    "decimals": 2,
    "symbol": "kr"
  },
  {
    "key": "SGD",
    "numeric": "702",
    "name": "Singapore Dollar",
    "shortName": "Dollar",
    "decimals": 2,
    "symbol": "$"
  },
  {
    "key": "SHP",
    "numeric": "654",
    "name": "Saint Helena Pound",
    "shortName": "Pound",
    "decimals": 2,
    "symbol": "£"
  },
  {
    "key": "SKK",
    "name": "Slovak Koruna",
    "symbol": "Sk"
  },
  {
    "key": "SLE",
    "numeric": "925",
    "name": "Sierra Leonean Leone",
    "shortName": "Leone",
    "decimals": 2,
    "symbol": "Le"
  },
  {
    "key": "SLL",
    "numeric": "694",
    "name": "Sierra Leonean Leone",
    "shortName": "Leone",
    "decimals": 2,
    "symbol": "Le"
  },
  {
    "key": "SOS",
    "numeric": "706",
    "name": "Somali Shilling",
    "shortName": "Shilling",
    "decimals": 2,
    "symbol": "S"
  },
  {
    "key": "SRD",
    "numeric": "968",
    "name": "Surinamese Dollar",
    "shortName": "Dollar",
    "decimals": 2,
    "symbol": "$"
  },
  {
    "key": "SSP",
    "numeric": "728",
    "name": "South Sudanese Pound",
    "shortName": "Pound",
    "decimals": 2,
    "symbol": "£"
  },
  {
    "key": "STN",
    "numeric": "930",
    "name": "Sao Tomean Dobra",
    "shortName": "Dobra",
    "decimals": 2,
    "symbol": "Db"
  },
  {
    "key": "SVC",
    "numeric": "222",
    "name": "Salvadoran Colón",
    "shortName": "Colón",
    "decimals": 2,
    "symbol": "₡"
  },
  {
    "key": "SYP",
    "numeric": "760",
    "name": "Syrian Pound",
    "shortName": "Pound",
    "decimals": 2,
    "symbol": "£"
  },
  {
    "key": "SZL",
    "numeric": "748",
    "name": "Swazi Lilangeni",
    "shortName": "Lilangeni",
    "decimals": 2,
    "symbol": "L"
  },
  {
    "key": "THB",
    "numeric": "764",
    "name": "Thai Baht",
    "shortName": "Baht",
    "decimals": 2,
    "symbol": "฿"
  },
  {
    "key": "TJS",
    "numeric": "972",
    "name": "Tajikistani Somoni",
    "shortName": "Somoni",
    "decimals": 2,
    "symbol": "ЅМ"
  },
  {
    "key": "TMT",
    "numeric": "934",
    "name": "Turkmenistani Manat",
    "shortName": "Manat",
    "decimals": 2,
    "symbol": "m"
  },
  {
    "key": "TND",
    "numeric": "788",
    "name": "Tunisian Dinar",
    "shortName": "Dinar",
    "decimals": 3,
    "symbol": "د"
  },
  {
    "key": "TOP",
    "numeric": "776",
    "name": "Tongan Pa'anga",
    "shortName": "Pa'anga",
    "decimals": 2,
    "symbol": "T"
  },
  {
    "key": "TRY",
    "numeric": "949",
    "name": "Turkish Lira",
    "shortName": "Lira",
    "decimals": 2,
    "symbol": "₺"
  },
  {
    "key": "TTD",
    "numeric": "780",
    "name": "Trinidad and Tobago Dollar",
    "shortName": "Dollar",
    "decimals": 2,
    "symbol": "TT$"
  },
  {
    "key": "TWD",
    "numeric": "901",
    "name": "New Taiwan Dollar",
    "shortName": "Dollar",
    "decimals": 2,
    "symbol": "NT$"
  },
  {
    "key": "TZS",
    "numeric": "834",
    "name": "Tanzanian Shilling",
    "shortName": "Shilling",
    "decimals": 2,
    "symbol": "Sh"
  },
  {
    "key": "UAH",
    "numeric": "980",
    "name": "Ukrainian Hryvnia",
    "shortName": "Hryvnia",
    "decimals": 2,
    "symbol": "₴"
  },
  {
    "key": "UGX",
    "numeric": "800",
    "name": "Ugandan Shilling",
    "shortName": "Shilling",
    "decimals": 2,
    "symbol": "Sh"
  },
  {
    "key": "USN",
    "numeric": "997",
    "name": "United States Dollar (Next day)",
    "shortName": "Dollar",
    "decimals": 2,
    "symbol": "$"
  },
  {
    "key": "USS",
    "name": "United States Dollar (Same day)",
    "shortName": "Dollar",
    "decimals": 2,
    "symbol": "$"
  },
  {
    "key": "UYI",
    "numeric": "940",
    "name": "Uruguay Peso en Unidades Indexadas",
    "decimals": 0,
    "symbol": "$U"
  },
  {
    "key": "UYU",
    "numeric": "858",
    "name": "Uruguayan Peso",
    "shortName": "Peso",
    "decimals": 2,
    "symbol": "$U"
  },
  {
    "key": "UYW",
    "numeric": "927",
    "name": "Unidad previsional",
    "decimals": 4,
    "symbol": "$U"
  },
  {
    "key": "UZS",
    "numeric": "860",
    "name": "Uzbekistan Som",
    "shortName": "Som",
    "decimals": 2,
    "symbol": "лв"
  },
  {
    "key": "VED",
    "numeric": "926",
    "name": "Venezuelan Bolívar Soberano (digital)",
    "shortName": "Bolívar",
    "decimals": 2,
    "symbol": "Bs.D"
  },
  {
    "key": "VEF",
    "numeric": "937",
    "name": "Venezuelan Bolívar Fuerte",
    "shortName": "Fuerte",
    "decimals": 2,
    "symbol": "Bs"
  },
  {
    "key": "VES",
    "numeric": "928",
    "name": "Venezuelan Bolívar Soberano",
    "shortName": "Bolívar",
    "decimals": 2,
    "symbol": "Bs.S"
  },
  {
    "key": "VND",
    "numeric": "704",
    "name": "Vietnamese Dong",
    "shortName": "Dong",
    "decimals": 1,
    "symbol": "₫"
  },
  {
    "key": "VUV",
    "numeric": "548",
    "name": "Vanuatu Vatu",
    "shortName": "Vatu",
    "decimals": 0,
    "symbol": "Vt"
  },
  {
    "key": "WST",
    "numeric": "882",
    "name": "Samoan Tala",
    "shortName": "Tala",
    "decimals": 2,
    "symbol": "T"
  },
  {
    "key": "XAF",
    "numeric": "950",
    "name": "Central African CFA Franc",
    "shortName": "CFA Franc",
    "decimals": 2,
    "symbol": "Franc"
  },
  {
    "key": "XAF",
    "numeric": "950",
    "name": "Central African CFA Franc",
    "shortName": "CFA Franc",
    "decimals": 2,
    "symbol": "Franc"
  },
  {
    "key": "XAG",
    "numeric": "961",
    "name": "Silver Ounce",
    "symbol": "Ounce"
  },
  {
    "key": "XAU",
    "numeric": "959",
    "name": "Gold Ounce",
    "symbol": "Ounce"
  },
  {
    "key": "XBA",
    "numeric": "955",
    "name": "European Composite Unit",
    "symbol": "EURCO"
  },
  {
    "key": "XBB",
    "numeric": "956",
    "name": "European Monetary Unit",
    "symbol": "E.M.U.-6"
  },
  {
    "key": "XBC",
    "numeric": "957",
    "name": "European Unit of Account 9",
    "symbol": "E.U.A.-9"
  },
  {
    "key": "XBD",
    "numeric": "958",
    "name": "European Unit of Account 17",
    "symbol": "E.U.A.-17"
  },
  {
    "key": "XCD",
    "numeric": "951",
    "name": "East Caribbean Dollar",
    "decimals": 2,
    "symbol": "$"
  },
  {
    "key": "XDR",
    "numeric": "960",
    "name": "IMF Special Drawing Rights",
    "symbol": "SDR"
  },
  {
    "key": "XFU",
    "name": "UIC Franc",
    "symbol": "Franc"
  },
  {
    "key": "XOF",
    "numeric": "952",
    "name": "West African CFA Franc",
    "shortName": "CFA Franc",
    "decimals": 2,
    "symbol": "Franc"
  },
  {
    "key": "XPD",
    "numeric": "964",
    "name": "Palladium Ounce",
    "symbol": "Ounce"
  },
  {
    "key": "XPF",
    "numeric": "953",
    "name": "CFP Franc",
    "shortName": "Franc",
    "decimals": 2,
    "symbol": "Franc"
  },
  {
    "key": "XPT",
    "numeric": "962",
    "name": "Platinum Ounce",
    "symbol": "Ounce"
  },
  {
    "key": "XSU",
    "numeric": "994",
    "name": "Sistema Unitario de Compensación Regional",
    "shortName": "SUCRE",
    "symbol": "Sucre"
  },
  {
    "key": "XTS",
    "numeric": "963",
    "name": "Code Reserved for Testing",
    "symbol": "XTS"
  },
  {
    "key": "XXX",
    "numeric": "999",
    "name": "Transaction",
    "symbol": "XXX"
  },
  {
    "key": "YER",
    "numeric": "886",
    "name": "Yemeni Rial",
    "shortName": "Rial",
    "decimals": 2,
    "symbol": "﷼"
  },
  {
    "key": "ZAR",
    "numeric": "710",
    "name": "South African Rand",
    "shortName": "Rand",
    "decimals": 2,
    "symbol": "R"
  },
  {
    "key": "ZMK",
    "name": "Zambian Kwacha",
    "shortName": "Kwacha",
    "decimals": 2,
    "symbol": "ZK"
  },
  {
    "key": "ZMW",
    "numeric": "967",
    "name": "Zambian Kwacha",
    "shortName": "Kwacha",
    "decimals": 2,
    "symbol": "ZK"
  },
  {
    "key": "ZWL",
    "numeric": "932",
    "name": "Zimbabwean Dollar",
    "shortName": "Dollar",
    "decimals": 2,
    "symbol": "$"
  }
];

export const SPREAD_ACTION_DELETE_ROW = 'delete row';
export const SPREAD_ACTION_DELETE_COLUMNS = 'delete columns';
export const SPREAD_ACTION_INVERT_ROWS = 'invert rows';
export const SPREAD_ACTION_TOGGLE_ROW = 'toggle row';
export const SPREAD_ACTION_TOGGLE_COLUMN = 'toggle column';
export const SPREAD_ACTION_EDIT_A_CELL = 'edited a cell';
export const SPREAD_ACTION_CUT_SELECTED = 'cutSelected';
export const SPREAD_ACTION_PASTE_SELECTED = 'pasteSelected';
export const SPREAD_ACTION_ADD_COLUMN = 'inside addColumnAtIdx';
export const SPREAD_ACTION_UNCATEGORIZE_ROW = 'inside uncategorizeSelectedRows';
export const SPREAD_ACTION_CATEGORIZE = 'categorized';
export const SPREAD_ACTION_CLEAR_CATEGORIZATION = 'clear categorization';
export const SPREAD_ACTION_COLUMN_METADATA_CHANGED = 'column metadata changed';
export const SPREAD_ACTION_ROW_LABEL_CHANGED = 'row label changed';
export const SPREAD_ACTION_ADJUSTMENT_CREATED = 'adjustment created';


export const LANGUAGES = {
    'af': 'Afrikaans',
    'sq': 'Albanian',
    'am': 'Amharic',
    'ar': 'Arabic',
    'hy': 'Armenian',
    'az': 'Azerbaijani',
    'eu': 'Basque',
    'be': 'Belarusian',
    'bn': 'Bengali',
    'bs': 'Bosnian',
    'bg': 'Bulgarian',
    'ca': 'Catalan',
    'ceb': 'Cebuano',
    'zh': 'Chinese',
    'zh-CN': 'Chinese',
    'zh-TW': 'Chinese',
    'co': 'Corsican',
    'hr': 'Croatian',
    'cs': 'Czech',
    'da': 'Danish',
    'nl': 'Dutch',
    'en': 'English',
    'eo': 'Esperanto',
    'et': 'Estonian',
    'fi': 'Finnish',
    'fr': 'French',
    'fy': 'Frisian',
    'gl': 'Galician',
    'ka': 'Georgian',
    'de': 'German',
    'el': 'Greek',
    'gu': 'Gujarati',
    'ht': 'Haitian Creole',
    'ha': 'Hausa',
    'haw': 'Hawaiian',
    'he': 'Hebrew',
    'iw': 'Hebrew',
    'hi': 'Hindi',
    'hmn': 'Hmong',
    'hu': 'Hungarian',
    'is': 'Icelandic',
    'ig': 'Igbo',
    'id': 'Indonesian',
    'ga': 'Irish',
    'it': 'Italian',
    'ja': 'Japanese',
    'jv': 'Javanese',
    'kn': 'Kannada',
    'kk': 'Kazakh',
    'km': 'Khmer',
    'rw': 'Kinyarwanda',
    'ko': 'Korean',
    'ku': 'Kurdish',
    'ky': 'Kyrgyz',
    'lo': 'Lao',
    'la': 'Latin',
    'lv': 'Latvian',
    'lt': 'Lithuanian',
    'lb': 'Luxembourgish',
    'mk': 'Macedonian',
    'mg': 'Malagasy',
    'ms': 'Malay',
    'ml': 'Malayalam',
    'mt': 'Maltese',
    'mi': 'Maori',
    'mr': 'Marathi',
    'mn': 'Mongolian',
    'my': 'Myanmar',
    'ne': 'Nepali',
    'no': 'Norwegian',
    'ny': 'Nyanja',
    'or': 'Odia',
    'ps': 'Pashto',
    'fa': 'Persian',
    'pl': 'Polish',
    'pt': 'Portuguese',
    'pa': 'Punjabi',
    'ro': 'Romanian',
    'ru': 'Russian',
    'sm': 'Samoan',
    'gd': 'Scots Gaelic',
    'sr': 'Serbian',
    'st': 'Sesotho',
    'sn': 'Shona',
    'sd': 'Sindhi',
    'si': 'Sinhala (Sinhalese)',
    'sk': 'Slovak',
    'sl': 'Slovenian',
    'so': 'Somali',
    'es': 'Spanish',
    'su': 'Sundanese',
    'sw': 'Swahili',
    'sv': 'Swedish',
    'tl': 'Tagalog (Filipino)',
    'tg': 'Tajik',
    'ta': 'Tamil',
    'tt': 'Tatar',
    'te': 'Telugu',
    'th': 'Thai',
    'tr': 'Turkish',
    'tk': 'Turkmen',
    'uk': 'Ukrainian',
    'ur': 'Urdu',
    'ug': 'Uyghur',
    'uz': 'Uzbek',
    'vi': 'Vietnamese',
    'cy': 'Welsh',
    'xh': 'Xhosa',
    'yi': 'Yiddish',
    'yo': 'Yoruba',
    'zu': 'Zulu'
}

export const USER_GUIDES = {
  // Borrower List
  FIND_BORROWER: { name: 'Searching for a Borrower', url: 'https://information.moodysanalytics.com/000000AJZUSRBLSUSR/managing-borrowing-entities/searching-for-a-borrower' },
  ADDING_BORROWERS: { name: 'Adding New Borrowers', url: 'https://information.moodysanalytics.com/000000AJZUSRBLSUSR/managing-borrowing-entities/adding-new-borrowers' },
  EDITING_BORROWER_INFO: { name: 'Editing Borrower Information', url: 'https://information.moodysanalytics.com/000000AJZUSRBLSUSR/managing-borrowing-entities/editing-borrower-information' },
  // Borrower Page (all tabs)
  UPLOADING_DOCUMENTS: { name: 'Uploading Documents', url: 'https://information.moodysanalytics.com/000000AJZUSRBLSUSR/uploading-documents' },
  PROCESSING_DOCUMENTS: { name: 'Processing Documents', url: 'https://information.moodysanalytics.com/000000AJZUSRBLSUSR/processing-automated-spreading-documents' },
  SUPPORTED_TAX_RETURNS: { name: 'Supported Tax Returns', url: 'https://information.moodysanalytics.com/000000AJZUSRBLSUSR/processing-tax-returns' },
  // Borrower Financials tab - available when financial data available
  VIEWING_DATA: { name: 'Viewing Data and Insights', url: 'https://information.moodysanalytics.com/000000AJZUSRBLSUSR/viewing-data' },
  REMOVING_DATA: { name: 'Removing Data', url: 'https://information.moodysanalytics.com/000000AJZUSRBLSUSR/removing-data' },
  // available when at least two periods of financial data available
  BUILD_STATEMENTS_FROM_EXISTING_DATA: { name: 'Building New Statements', url: 'https://information.moodysanalytics.com/000000AJZUSRBLSUSR/building-new-statements-from-existing-data' },
  VIEWING_CHANGE_LOG: { name: 'Viewing Change Log', url: 'https://information.moodysanalytics.com/000000AJZUSRBLSUSR/audit-log' },
  // Borrower Analysis tab
  CREATING_ANALYSES: { name: 'Creating Analyses', url: 'https://information.moodysanalytics.com/000000AJZUSRBLSUSR/using-financial-analysis' },
  // Digitization Flow
  // table id step (old flow label)
  IDENTIFYING_TABLES: { name: 'Identifying Tables', url: 'https://information.moodysanalytics.com/000000AJZUSRBLSUSR/processing-automated-spreading-documents/table-identification' },
  // table id step (new flow label)
  CLASSIFYING_TABLES: { name: 'Classifying Tables', url: 'https://information.moodysanalytics.com/000000AJZUSRBLSUSR/processing-automated-spreading-documents/table-identification' },
  // column adjustment step
  ADJUSTING_COLUMNS: { name: 'Adjusting Columns', url: 'https://information.moodysanalytics.com/000000AJZUSRBLSUSR/processing-automated-spreading-documents/column-adjustment' },
  // data review step (old flow label)
  FORMATTING_DATA: { name: 'Reviewing Data', url: 'https://information.moodysanalytics.com/000000AJZUSRBLSUSR/processing-automated-spreading-documents/data-review' },
  // data review step (new flow label)
  FORMATTING_DATA_EARLY_CLASS: { name: 'Formatting Data', url: 'https://information.moodysanalytics.com/000000AJZUSRBLSUSR/processing-automated-spreading-documents/data-review' },
  CORRECTING_DATA: { name: 'Correcting Values', url: 'https://information.moodysanalytics.com/000000AJZUSRBLSUSR/processing-automated-spreading-documents/data-categorization/correcting-data-in-data-categorization' },
  // categorization step (old flow label)
  CATEGORIZING_DATA: { name: 'Categorizing Data', url: 'https://information.moodysanalytics.com/000000AJZUSRBLSUSR/processing-automated-spreading-documents/data-categorization' },
  // categorization step (new flow label)
  REVIEW_MAPPINGS_GUIDE: { name: 'Mapping Data', url: 'https://information.moodysanalytics.com/000000AJZUSRBLSUSR/processing-automated-spreading-documents/data-categorization' },
  MERGING_DATA: { name: 'Merging a New Table', url: 'https://information.moodysanalytics.com/000000AJZUSRBLSUSR/processing-automated-spreading-documents/data-categorization/merging-data-in-data-categorization' },
  TROUBLESHOOTING_SPREAD: { name: 'Troubleshooting your Spread', url: 'https://information.moodysanalytics.com/000000AJZUSRBLSUSR/processing-automated-spreading-documents/data-categorization/troubleshooting-a-spread' },
  MAKING_ADJUSTMENTS: { name: 'Making Adjustments', url: 'https://information.moodysanalytics.com/000000AJZUSRBLSUSR/processing-automated-spreading-documents/data-categorization/making-adjustments-to-your-spread' },
  SPREAD_ASSIST: { name: 'Using Spread Assist', url: 'https://information.moodysanalytics.com/000000AJZUSRBLSUSR/processing-automated-spreading-documents/data-categorization/using-spread-assist' },
  COMMENTING: { name: 'Commenting', url: 'https://information.moodysanalytics.com/000000AJZUSRBLSUSR/processing-automated-spreading-documents/data-categorization/commenting-in-a-spread' },
}

export const SCENARIO_TYPE_HISTORICAL = 'HISTORICAL';
export const SCENARIO_TYPE_PROJECTION = 'PROJECTION';
export const SCENARIO_TYPE_PROFORMA = 'PROFORMA';

export const PREPARATION_TYPE_ANALYST_KEY = 'ANALYST';
export const PREPARATION_TYPE_CPA_PREP_KEY = 'CPA_PREP';
export const PREPARATION_TYPE_MANAGEMENT_KEY = 'MANAGEMENT';
export const PREPARATION_TYPE_NTR_KEY = 'NTR';
export const PREPARATION_TYPE_REVIEWED_KEY = 'REVIEWED';
export const PREPARATION_TYPE_QUALIFIED_KEY = 'QUALIFIED';
export const PREPARATION_TYPE_TAX_RETURN_KEY = 'TAX_RETURN';
export const PREPARATION_TYPE_UNKNOWN_KEY = 'UNKNOWN';
export const PREPARATION_TYPE_UNQUALIFIED_KEY = 'UNQUALIFIED';

export const PREPARATION_TYPE_ANALYST_VALUE = 'Analyst';
export const PREPARATION_TYPE_CPA_PREP_VALUE = 'CPA Prepared';
export const PREPARATION_TYPE_MANAGEMENT_VALUE = 'Management';
export const PREPARATION_TYPE_NTR_VALUE = 'NTR';
export const PREPARATION_TYPE_QUALIFIED_VALUE = 'Qualified';
export const PREPARATION_TYPE_REVIEWED_VALUE = 'Reviewed';
export const PREPARATION_TYPE_TAX_RETURN_VALUE = 'Tax Return';
export const PREPARATION_TYPE_UNKNOWN_VALUE = 'Unknown';
export const PREPARATION_TYPE_UNQUALIFIED_VALUE = 'Unqualified';

export const PREPARATION_TYPES_FOR_PROJECTIONS = {
  [PREPARATION_TYPE_ANALYST_KEY]: PREPARATION_TYPE_ANALYST_VALUE,
  [PREPARATION_TYPE_CPA_PREP_KEY]: PREPARATION_TYPE_CPA_PREP_VALUE,
  [PREPARATION_TYPE_MANAGEMENT_KEY]: PREPARATION_TYPE_MANAGEMENT_VALUE
};

export const PREPARATION_TYPES_FOR_HISTORICAL = {
  [PREPARATION_TYPE_UNQUALIFIED_KEY]: PREPARATION_TYPE_UNQUALIFIED_VALUE,
  [PREPARATION_TYPE_QUALIFIED_KEY]: PREPARATION_TYPE_QUALIFIED_VALUE,
  [PREPARATION_TYPE_TAX_RETURN_KEY]: PREPARATION_TYPE_TAX_RETURN_VALUE,
  [PREPARATION_TYPE_REVIEWED_KEY]: PREPARATION_TYPE_REVIEWED_VALUE,
  [PREPARATION_TYPE_CPA_PREP_KEY]: PREPARATION_TYPE_CPA_PREP_VALUE,
  [PREPARATION_TYPE_NTR_KEY]: PREPARATION_TYPE_NTR_VALUE,
  [PREPARATION_TYPE_MANAGEMENT_KEY]: PREPARATION_TYPE_MANAGEMENT_VALUE,
  [PREPARATION_TYPE_UNKNOWN_KEY]: PREPARATION_TYPE_UNKNOWN_VALUE,
};

export const STATEMENT_DATE = 'STATEMENT DATE';
export const SCENARIO_TYPE = 'SCENARIO TYPE';
export const SCENARIO_TYPES = [
  SCENARIO_TYPE_HISTORICAL,
  SCENARIO_TYPE_PROJECTION
];

export const MANUAL_REVIEW_HEADER_ORDER = {
  STATEMENT_DATE_INDEX: 0,
  REPORTING_INTERVAL_INDEX: 1,
  SCENARIO_TYPE_INDEX: 2,
  PREPARATION_TYPE_INDEX: 3,
  PREPARED_BY_INDEX: 4
};

export const PREPARED_BY = 'ANALYST';
export const PREPARED_BY_LEGACY = 'PREPARED BY';

export const LEGACY_META_LABELS = new Map<string, string>([
  [PREPARED_BY_LEGACY, PREPARED_BY]
]);

export function canonicalMetaLabel(label: string): string {
  return LEGACY_META_LABELS.get(label) ?? label;
}

export const MANUAL_REVIEW_HEADERS = [
  STATEMENT_DATE,
  'REPORTING INTERVAL',
  SCENARIO_TYPE,
  'PREPARATION TYPE',
  PREPARED_BY
];

export const MANUAL_REVIEW_HEADERS_INCLUDING_LEGACY = [
  ...MANUAL_REVIEW_HEADERS,
  PREPARED_BY_LEGACY
];

export const REPORTING_INTERVAL_ANNUAlLY_KEY = 'ANNUALLY';
export const REPORTING_INTERVAL_FISCAL_YTD_KEY = 'FISCAL_YTD';
export const REPORTING_INTERVAL_MONTHLY_KEY = 'MONTHLY';
export const REPORTING_INTERVAL_QUARTERLY_KEY = 'QUARTERLY';
export const REPORTING_INTERVAL_SEMI_ANNUALLY_KEY = 'SEMI_ANNUALLY';

export const REPORTING_INTERVAL_ANNUALLY_VALUE = 'ANNUALLY';
export const REPORTING_INTERVAL_FISCAL_YTD_VALUE = 'FISCAL_YTD';
export const REPORTING_INTERVAL_MONTHLY_VALUE = 'MONTHLY';
export const REPORTING_INTERVAL_QUARTERLY_VALUE = 'QUARTERLY';
export const REPORTING_INTERVAL_SEMI_ANNUALLY_VALUE = 'SEMI_ANNUALLY';

export const REPORTING_INTERVALS = {
  [REPORTING_INTERVAL_ANNUAlLY_KEY]: REPORTING_INTERVAL_ANNUALLY_VALUE,
  [REPORTING_INTERVAL_FISCAL_YTD_KEY]: REPORTING_INTERVAL_FISCAL_YTD_VALUE,
  [REPORTING_INTERVAL_MONTHLY_KEY]: REPORTING_INTERVAL_MONTHLY_VALUE,
  [REPORTING_INTERVAL_QUARTERLY_KEY]: REPORTING_INTERVAL_QUARTERLY_VALUE,
  [REPORTING_INTERVAL_SEMI_ANNUALLY_KEY]: REPORTING_INTERVAL_SEMI_ANNUALLY_VALUE
};

export const ProjectionCellStyle = {
  backgroundColor: '#68C4FF',
  color: '#0000FF'
};

export const ProjectionRowStyle = {
  backgroundColor: '#68C4FF',
}

export const PROBLEM_CELL_STYLE = {
  backgroundColor: '#e69d9d',
  color: '#000000',
};

export const WARNING_CELL_STYLE = {
  backgroundColor: '#f9e29b',
  color: '#000000',
};

export const OPTION_LABEL_CELL_STYLE = {
  backgroundColor: '#D3D3D3',
  color: '#008000',
  'font-weight': 'bold',
}

export const DISABLED_HEADER_VALUE_STYLE = {
  backgroundColor: '#D3D3D3',
  color: '#000000',
}

export const ACCOUNTING_INTEGRATION = {
  QBO: 'qbo',
  XERO: 'xero'
}

const CURRENCY = 'CURRENCY';
const RATIO = 'RATIO';
const NUMBER = 'NUMBER';
const PERCENT = 'PERCENT';
const DAYS = 'DAYS';

export const TEXT_FORMAT_TYPES = {
  CURRENCY,
  DAYS,
  RATIO,
  NUMBER,
  PERCENT,
};

export const SPREADING_TEMPLATE_TYPES = {
  STANDARD: 'STANDARD',
  DEBT_SERVICE_COVERAGE: 'DSCR',
  GLOBAL_CASHFLOW: 'GLOBAL_CASHFLOW'
}

// These are the URLS that we allow to send requests to the
// embedded flow and access pages w/ the embedded login scheme.
// To be kept in sync with the backend
// That file is at whitemamba/lib/partner/embedded.py
export const ACCEPTABLE_URLS = [
  // fincura
  '*.fincura.com',
  // numerated - dev
  '*.ngtdev.com',
  '*.ngtdevel.com',
  '*.ngtqa.com',
  // numerated cocc uat/prod
  '*.cocci.com',
  // numerated aws uat
  '*.expressbanking-uat.net',
  // numerated aws prod
  '*.expressbanking.net',
  // numerated fis uat/prod
  '*.expressbankloan.com',
  // newtek dev/prod
  '*.newtekreferrals.com',
  'numerated-uat-spreading.com',
  'numeratedspreading.com'
]

export const PLATFORM_PERMISSIONS = {
  MANAGE_BORROWER: 'manage_borrower',
  MANAGE_PORTFOLIO: 'manage_portfolio',
  ADD_BORROWER_TO_PORTFOLIO: 'add_borrower_to_portfolio',
  MANAGE_USER: 'manage_user',
  EDIT_SPREADS_AND_RUN_ANALYSIS: 'edit_spreads_and_run_analysis',
  VIEW_SUBMISSION_REVIEW_QUEUE: 'view_submission_review_queue',
  VIEW_DOCUMENT_STATUS_TRACKER: 'view_document_status_tracker',
  VIEW_SPREADS_AND_ANALYSIS: 'view_spreads_and_analysis',
  VIEW_COMPANY_DETAILS: 'view_company_details',
  MANAGE_DOCUMENT: 'manage_document',
  DELETE_DOCUMENT: 'delete_document',
  MANAGE_TEMPLATE: 'manage_template',
  ACCESS_DEVELOPER_TOOLS: 'access_developer_tools',
  RUN_REPORTING: 'run_reporting',
  SEARCH_BORROWER: "search_borrower",
  GENERATE_API_TOKEN: "generate_api_token"
}

export const COMPANY_PAGE_VIEW_MODE = {
  NO_ACCESS: "no_access",
  // The entitlement lookup failed, so access is unknown rather than denied 
  ENTITLEMENT_UNAVAILABLE: "entitlement_unavailable",
  VIEW_ONLY: "view_only",
  FULL: "full"
}

export const COMPANY_PAGE_ACCESS_ERROR_TEXT = {
  [COMPANY_PAGE_VIEW_MODE.NO_ACCESS]: "You don't have enough permissions to view this page.",
  [COMPANY_PAGE_VIEW_MODE.ENTITLEMENT_UNAVAILABLE]: "We couldn't confirm your access. Please wait a moment and try again. If the problem continues, contact support.",
}

export const COMPANY_TABS = {
  OVERVIEW: "overview",
  FINANCIALS: "financials",
  ANALYSES: "analyses",
  DOCUMENT_LIST: "documentList",
  PROFILE: "profile",
  BORROWER_NAV: 'borrowerNav',
}

export const COMPANY_ENTITLEMENT_DATA = {
  PAGE_VIEW_MODE: 'pageViewMode',
  SHOW_EDIT_SPREAD_OPTIONS: 'showEditSpreadOptions',
  SHOW_DELETE_DOCUMENT_BUTTON: 'showDeleteDocumentButton',
  HAS_DOCUMENT_MANAGE_PERMISSION: 'hasDocumentManagePermission',
  CAN_ASSIGN_BORROWER_TO_PORTFOLIO: 'canAssignBorrowerToPortfolio',
  SHOW_ADD_STATEMENT_BUTTON: 'showAddStatementButton',
  SHOW_BUILD_NEW_STATEMENT_BUTTON: 'showBuildNewStatementButton',
  SHOW_REQUEST_STATEMENTS_BUTTON: 'showRequestStatementsButton',
}

export const NON_SPREADABLE_COLUMN_PREP_TYPES = [
  "COMBINED",
  "CALCULATED",
  "UNQUALIFIED_IMPORT",
  "QUALIFIED_IMPORT",
  "TAX_RETURN_IMPORT",
  "REVIEWED_IMPORT",
  "CPA_PREP_IMPORT",
  "NTR_IMPORT",
  "MANAGEMENT_IMPORT",
  "ANALYST_IMPORT",
  "IMPORTED",
  "FORECASTED",
  "ANNUALIZED"
]

const BASE_NON_FORM_ELIGIBLE_COLUMN_PREP_TYPES = [
  "COMBINED",
  "CALCULATED",
  "UNQUALIFIED_IMPORT",
  "QUALIFIED_IMPORT",
  "TAX_RETURN_IMPORT",
  "REVIEWED_IMPORT",
  "CPA_PREP_IMPORT",
  "NTR_IMPORT",
  "MANAGEMENT_IMPORT",
  "ANALYST_IMPORT",
  "IMPORTED",
]

/**
 * Preparation types excluded from the Consolidate Periods form.
 * Unlike NON_SPREADABLE_COLUMN_PREP_TYPES, FORECASTED and ANNUALIZED are both
 * allowed here so that Statement Builder–created projections and Annualize
 * Period statements can be consolidated (CITI-2474).
 */
export const NON_CONSOLIDATABLE_COLUMN_PREP_TYPES = [
  ...BASE_NON_FORM_ELIGIBLE_COLUMN_PREP_TYPES,
]

/**
 * Preparation types excluded from the Calculate a Missing Period form.
 * FORECASTED and ANNUALIZED are both intentionally omitted — Projection/Proforma
 * statements created via "Create a Scenario" or "Annualize" may be used as inputs
 */
export const NON_MISSING_PERIOD_COLUMN_PREP_TYPES = [
  ...BASE_NON_FORM_ELIGIBLE_COLUMN_PREP_TYPES,
]

export const DEFAULT_IMAGE_CROPPER = {
  x1: -100,
  y1: -100,
  x2: 10000,
  y2: 10000,
  width: 0,
  height: 0,
};

export const SUPPORTED_VIEW_SOURCE_FILE_TYPES = [
  'PDF_TEXT',
  'PDF_IMAGE',
  'IMAGE',
];


export const MONTH_FINAL_DAYS = {
  1: 31,
  2: 28,
  3: 31,
  4: 30,
  5: 31,
  6: 30,
  7: 31,
  8: 31,
  9: 30,
  10: 31,
  11: 30,
  12: 31
}
export const LEAP_YEAR_MONTH_FINAL_DAYS = {
  1: 31,
  2: 29,
  3: 31,
  4: 30,
  5: 31,
  6: 30,
  7: 31,
  8: 31,
  9: 30,
  10: 31,
  11: 30,
  12: 31
}

export const UNKNOWN_ERROR_TOAST_BODY = "An unknown error has occurred. Our team has been notified and is investigating the issue.";
export const UNKNOWN_ERROR_TOAST_TITLE = "Unknown Error";

export const SCENARIO_TYPE_OPTIONS: Select2OptionData[] = [
  { id: 'HISTORICAL', text: 'Historical' },
  { id: 'PROJECTION', text: 'Projection' },
  { id: 'PROFORMA', text: 'Proforma' },
];

export const NON_HISTORICAL_SCENARIO_TYPE_OPTIONS: Select2OptionData[] =
  SCENARIO_TYPE_OPTIONS.filter(o => o.id !== 'HISTORICAL');


export const SPREAD_ORIGIN_CHOICES = {
  BATCH_SOURCE_S_AND_P_FEED: 'S_AND_P_FEED',
  SPREAD_ORIGIN_USER_UPLOAD: 'user_upload'
}